<template>
  <div>
    <div
      v-if="!rented && total > 0">
      <my-case-default-payment-updater/>
    </div>
    <h5 class="mt-lgp">
      Purchase Summary
    </h5>

    <div class="mt-md">
      <div class="d-flex">
        <PreviewImageButton
          :src="styleColor.images.front.url"
          size="tiny"/>
        <div class="ms-md w-100 small-text">
          <RouterLink
            :to="{ name: 'brand', params: { name: slugify(styleColor.style.brand), brandId: styleColor.style.brandId } }"
            class="text-link text-armor">
            {{ styleColor.style.brand }}
          </RouterLink>
          <h6 class="mb-0">
            {{ styleColor.style.fullName }}
          </h6>
          <p class="small-text text-pewter">
            Size {{ itemToRent.item.trueSize }}
          </p>
        </div>
      </div>
      <div class="line-item">
        <p>Price</p>
        <p>{{ formatPrice(quarterlyRentalAmount) }}</p>
      </div>
      <div class="line-item">
        <p>Tax</p>
        <p>{{ taxDisplay }}</p>
      </div>
      <div
        v-for="lineItem in previewData?.lineItems"
        :key="lineItem.description"
        class="line-item promo mt-sm">
        <div>{{ lineItem.description }}</div>
        <div>-{{ formatPrice(lineItem.amount) }}</div>
      </div>
      <hr>
      <div class="line-item">
        <p v-if="rented">
          Paid with {{ defaultPayment.brand }} ending in {{ defaultPayment.last4 }}
        </p>
        <template v-else>
          <b>Total</b>
          <b>{{ formatPrice(total) }}</b>
        </template>
        <hr
          v-if="previewData?.otherItems && previewData.otherItems.length > 0"
          class="w-100">
        <div
          v-for="otherItem in previewData?.otherItems"
          :key="otherItem.description"
          class="line-item mt-sm text-plum">
          <div>
            <p class="semi-bold">
              {{ otherItem.description }}
            </p>
            <p
              v-if="otherItem.message"
              class="pt-xs">
              {{ otherItem.message }}
            </p>
          </div>
          <p v-if="otherItem.amount > 0">
            {{ formatPrice(otherItem.amount) }}
          </p>
        </div>
      </div>
    </div>
    <GiftCardPromoCodeInput
      v-if="!rented"
      :disabled="disabled"
      :accepts="CodeInputType.GiftCard"
      @applied="handleApplied"
      @applying="handleApplying"/>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { formatPrice, slugify } from '@/utils/stringParsing.js'
import { useQuarterlyRentalsStore } from '@/stores/quarterlyRentals.js'
import { useClosetStore } from '@shared/stores/closet.js'
import { useClientStore } from '@shared/stores/client.js'
import MyCaseDefaultPaymentUpdater from '../common/MyCaseDefaultPaymentUpdater'
import PreviewImageButton from '@shared/components/ADORN/PreviewImageButton'
import GiftCardPromoCodeInput from '@/components/global/GiftCardPromoCodeInput.vue'
import { CodeInputType } from '@/composables/useGiftCardPromoCode.js'

const props = defineProps({
  itemToRent: {
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  rented: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['total-updated', 'price-loading'])

const { getRentForLongerPreview } = useQuarterlyRentalsStore()
const closetStore = useClosetStore()
const clientStore = useClientStore()

const previewData = ref(null)

const styleColorsMap = computed(() => closetStore.styleColorsMap)
const defaultPayment = computed(() => clientStore.defaultPayment)

const styleColor = computed(() => {
  return styleColorsMap.value[props.itemToRent.item.styleColor]
})

const quarterlyRentalAmount = computed(() => {
  // Use preview data if available, otherwise fall back to item data
  if (previewData.value?.client) {
    return previewData.value.client
  }
  if (props.itemToRent.extensionEligibility?.amount) {
    return props.itemToRent.extensionEligibility.amount
  }
  return props.itemToRent.canRentForLonger?.amount || 0
})

const taxDisplay = computed(() => {
  if (previewData.value === null || previewData.value.tax === undefined) { return '---' }
  return '$' + (previewData.value.tax / 100).toFixed(2)
})

const remainingCredit = computed(() => {
  if (previewData.value === null) { return 0 }
  return previewData.value?.otherItems?.find(item => item.description === 'Remaining account credit')?.amount || 0
})

const total = computed(() => {
  if (previewData.value?.chargeAmount !== undefined) {
    return previewData.value.chargeAmount
  }
  return quarterlyRentalAmount.value
})

const fetchPreview = async () => {
  if (props.itemToRent && props.itemToRent.id) {
    emit('price-loading', true)
    try {
      const result = await getRentForLongerPreview(props.itemToRent.id)
      previewData.value = result.data
      emit('total-updated', total.value, remainingCredit.value)
    } catch (error) {
      previewData.value = null
      emit('total-updated', total.value, remainingCredit.value)
    } finally {
      emit('price-loading', false)
    }
  }
}

const handleApplied = async (event) => {
  // We only accept gift cards for quarterly rental purchases
  if (event.type === CodeInputType.GiftCard) {
    await fetchPreview()
  }
}

const handleApplying = (isApplying) => {
  emit('price-loading', isApplying)
}

onMounted(() => {
  fetchPreview()
})
</script>

<style lang="scss" scoped>
.line-item {
  display: flex;
  justify-content: space-between;

  p {
    margin: 0;
  }
}

.promo {
  color: $success-light;
  font-weight: $font-weight-bold;
}

</style>

